import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dataProvider } from "../../Provider/parseDataProvider";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
];
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string
    .split(/[ _]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const EditTicketDialog = ({ open, onClose, ticket, onSuccess }) => {
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Initialize form with ticket data when dialog opens
  useEffect(() => {
    if (ticket && open) {
      setStatus(ticket.status || "");
      setRemarks(ticket.remarks || "");
      setErrors({});
      setSubmitError("");
    }
  }, [ticket, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!status) {
      newErrors.status = "Status is required";
    }

    if (remarks && remarks.trim().length > 0) {
      if (remarks.trim().length < 5) {
        newErrors.remarks = "Remarks must be at least 5 characters";
      } else if (remarks.trim().length > 500) {
        newErrors.remarks = "Remarks must not exceed 500 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const result = await dataProvider.updateTicket({
        ticketId: ticket.id,
        status: status,
        remarks: remarks.trim(),
      });

      if (result.success) {
        // Call success callback to refresh the list
        if (onSuccess) {
          onSuccess(result.data);
        }
        handleClose();
      } else {
        setSubmitError(result.message || "Failed to update ticket");
        if (result.errors) {
          setErrors(result.errors);
        }
      }
    } catch (error) {
      console.error("Error updating ticket:", error);
      setSubmitError(error.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setStatus("");
      setRemarks("");
      setErrors({});
      setSubmitError("");
      onClose();
    }
  };

  if (!ticket) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#1A1A1A",
          color: "#FFF",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          color: "#FFF",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Ticket - {ticket.ticketId || ticket.id}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={isSubmitting}
          sx={{
            color: "#FFF",
            "&:hover": {
              transform: "rotate(90deg)",
              transition: "transform 0.3s",
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderColor: "rgba(255, 239, 153, 0.20)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Ticket Information */}
          <Box
            sx={{
              p: 2,
              bgcolor: "rgba(255, 215, 0, 0.05)",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "rgba(255, 239, 153, 0.20)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: "#FFD700" }}
            >
              Ticket Information
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <strong style={{ color: "#FFD700" }}>Username:</strong>{" "}
              {ticket.username}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <strong style={{ color: "#FFD700" }}>Role:</strong>{" "}
              {ticket.role || "N/A"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <strong style={{ color: "#FFD700" }}>Category:</strong>{" "}
              {capitalizeFirstLetter(ticket.category)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 1 }}
            >
              <strong style={{ color: "#FFD700" }}>Description:</strong>{" "}
              {ticket.description}
            </Typography>
          </Box>

          {/* Status Dropdown */}
          <Box>
            <TextField
              select
              fullWidth
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setErrors((prev) => ({ ...prev, status: "" }));
              }}
              error={!!errors.status}
              helperText={errors.status}
              required
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  color: "#FFF",
                  "& fieldset": {
                    borderColor: "rgba(255, 239, 153, 0.20)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FFD700",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FFD700",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "0.75em",
                  "&.Mui-focused": {
                    color: "#FFD700",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiSelect-icon": {
                  color: "#FFD700",
                },
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Remarks Field */}
          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              placeholder="Add remarks or comments about this ticket..."
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                setErrors((prev) => ({ ...prev, remarks: "" }));
              }}
              error={!!errors.remarks}
              helperText={
                errors.remarks ||
                `${remarks.length}/500 characters (minimum 5 if provided)`
              }
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  color: "#FFF",
                  "& fieldset": {
                    borderColor: "rgba(255, 239, 153, 0.20)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#FFD700",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FFD700",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "0.75em",
                  "&.Mui-focused": {
                    color: "#FFD700",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255, 255, 255, 0.5)",
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Error Alert */}
          {submitError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid rgba(255, 239, 153, 0.20)" }}
      >
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{
            textTransform: "capitalize",
            borderRadius: 1.5,
            flex: 1,
            border: "1px solid rgba(255, 239, 153, 0.20)",
            color: "#FFF",
            "&:hover": {
              border: "1px solid #fff",
              background: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            textTransform: "capitalize",
            borderRadius: 1.5,
            flex: 1,
            minWidth: 100,
            backgroundColor: "#FFD700",
            color: "#000",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "#FFA000",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(255, 215, 0, 0.3)",
              color: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: "#000" }} />
          ) : (
            "Update Ticket"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTicketDialog;
